/* eslint-disable react/display-name */
import React, { useCallback, useMemo, useState } from 'react';
import { useTheme, Tooltip, IconInfo } from '@veneer/core';
import {
  getDocumentUrl,
  useFeatureFlagContext
} from '@eu-data-act/react-euda-commons/dist/index';

import {
  LearnMoreButtonContainer,
  ScreenReaderOnly
} from '../device-item/styled';
import { PlainButton } from '../common/styled';

import { useDependencyManagerContext } from '../../contexts/dependencyManager';
import { getFileName } from '../../utils/get-document-name';

type PDFLearnMoreButtonProps = {
  categoryType: string;
  deviceTelemetryFormat: string;
  'data-testid': string;
  tabIndex?: number;
};

const PDFLearnMoreButton: React.FC<PDFLearnMoreButtonProps> = React.memo(
  (props) => {
    const {
      categoryType,
      deviceTelemetryFormat,
      'data-testid': dataTestId,
      tabIndex
    } = props;
    const theme = useTheme();
    const {
      translate,
      northboundAPIs: {
        v1: {
          localization: { country, language }
        }
      }
    } = useDependencyManagerContext();
    const { releasePlatformHpAnalytics } = useFeatureFlagContext();

    const documentName = getFileName(categoryType, deviceTelemetryFormat);

    const [isLoading, setIsLoading] = useState(false);
    const [documentAvailable, setDocumentAvailable] = useState<boolean | null>(
      null
    );

    const learnMoreAnalyticsAttributes = useMemo(() => {
      return releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'document',
            'data-gtm-id': documentName,
            'data-gtm-value': 'PDF',
            'data-gtm-action': `view ${categoryType} ${deviceTelemetryFormat} document`
          }
        : {};
    }, [
      releasePlatformHpAnalytics,
      documentName,
      categoryType,
      deviceTelemetryFormat
    ]);

    const handleLearnMoreClick = useCallback(async () => {
      if (isLoading) return;

      try {
        setIsLoading(true);
        const { isDocumentAvailable, documentUrl } = await getDocumentUrl({
          documentName,
          country,
          language
        });

        setDocumentAvailable(isDocumentAvailable);

        if (isDocumentAvailable && documentUrl) {
          window.open(documentUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        setDocumentAvailable(false);
      } finally {
        setIsLoading(false);
      }
    }, [documentName, country, language, isLoading]);

    return (
      <LearnMoreButtonContainer $veneerTheme={theme}>
        <PlainButton
          $veneerTheme={theme}
          appearance="ghost"
          data-testid={dataTestId}
          onClick={handleLearnMoreClick}
          disabled={isLoading || documentAvailable === false}
          tabIndex={tabIndex}
          aria-label={translate(
            'components.device-item.learn-more-button.aria-label',
            { categoryType }
          )}
          aria-describedby={`${dataTestId}-description`}
          {...learnMoreAnalyticsAttributes}
        >
          {translate('components.device-item.learn-more-button.text')}
          <ScreenReaderOnly
            id={`${dataTestId}-description`}
            aria-live="polite"
          >
            {translate(
              'components.device-item.learn-more-button.aria-description',
              { categoryType }
            )}
          </ScreenReaderOnly>
        </PlainButton>
        {documentAvailable === false && (
          <Tooltip
            contentHideDelayOnHover={300}
            contentShowDelayOnHover={300}
            placement="top-end"
            content={
              <span>
                {translate(
                  'components.device-item.learn-more-button.unavailable'
                )}
              </span>
            }
          >
            <IconInfo />
          </Tooltip>
        )}
      </LearnMoreButtonContainer>
    );
  }
);

export default PDFLearnMoreButton;
